import { parseWords, type Script, type Word } from "./script.ts";

/** Cues inherit script defaults. Each cue may be one word or an entire passage. */
export type PromptDocument = {
  version: 1;
  title: string;
  mode?: "teleprompter" | "cue-cards";
  defaults?: { wpm?: number; intensity?: "soft" | "natural" | "strong" };
  cues: {
    text: string;
    wpm?: number;
    intensity?: "soft" | "natural" | "strong";
    pauseAfterMs?: number;
  }[];
};
const INTENSITIES = ["soft", "natural", "strong"];
export const MAX_IMPORT_BYTES = 256000;
export const EXAMPLE_JSON = JSON.stringify(
  {
    version: 1,
    title: "An idea worth sharing",
    defaults: { wpm: 140, intensity: "natural" },
    cues: [
      {
        text: "Take a breath.",
        wpm: 100,
        intensity: "soft",
        pauseAfterMs: 600,
      },
      { text: "You have something", wpm: 135 },
      {
        text: "worth sharing.",
        wpm: 110,
        intensity: "strong",
        pauseAfterMs: 400,
      },
      { text: "\n\nLet’s begin.", wpm: 150 },
    ],
  } satisfies PromptDocument,
  null,
  2,
);

export const LLM_PROMPT = `Help me write a natural spoken script for Cue, a teleprompter app. I will explain my topic, audience, tone, and desired length after this instruction. Write what I should say, with thoughtful pacing, emphasis, and pauses. Return ONLY one valid JSON object, with no Markdown or explanation.

Use this format:
${EXAMPLE_JSON}

Rules:
- Optional mode is "teleprompter" (default) or "cue-cards". In cue-cards mode each cue is one static card advanced by tapping. Write concise ideas rather than a spoken script; wpm is retained but ignored, and pauseAfterMs is guidance only. Paragraph breaks within a cue stay on the same card.
- version must be the number 1. title is a short nonempty string, at most 100 characters.
- defaults is optional; defaults.wpm is the reading speed (words per minute), and defaults.intensity is the emphasis. Omitted defaults mean 140 wpm and "natural".
- cues is a nonempty array, in speaking order. Each cue has a nonempty text string. A cue can be a single word, phrase, sentence, or paragraph. Split a passage into adjacent cues when a specific word needs its own direction.
- A cue can override wpm or intensity; omitted values inherit defaults. wpm must be a number from 60 to 240. intensity must be exactly "soft", "natural", or "strong".
- Optional pauseAfterMs is an integer from 0 to 10000: a silent beat AFTER the last word of that cue. It defaults to 0. Use pauses to shape tempo, independently of speaking speed.
- Adjacent cues are separated by one space unless text already includes boundary whitespace. Put \\n\\n inside text to start a new paragraph. Do not repeat text between cues.
- Use only the documented keys. Do not include timestamps, SSML, stage directions, or commentary in text; every text string is spoken aloud.
- Keep the complete script under 30000 characters and 10000 words. Use emphasis selectively, and choose the pace and pauses to fit my intent. Estimate the requested duration using word count / wpm plus pauses.

My topic, audience, tone, desired duration, and points to cover:
[Describe what you want to say here]`;

type ObjectValue = Record<string, unknown>;
function object(value: unknown, path: string, keys: string[]): ObjectValue {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error(`${path} must be an object.`);
  for (const key of Object.keys(value)) {
    if (!keys.includes(key))
      throw new Error(`${path}.${key} is not supported.`);
  }
  // SAFETY: Checked object above; property values remain unknown until field validation.
  return value as ObjectValue;
}
function pace(value: unknown, path: string, fallback: number): number {
  if (value === undefined) return fallback;
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < 60 ||
    value > 240
  )
    throw new Error(`${path} must be a number from 60 to 240.`);
  return value;
}
function intensity(value: unknown, path: string, fallback: number): number {
  if (value === undefined) return fallback;
  const index = typeof value === "string" ? INTENSITIES.indexOf(value) : -1;
  if (index < 0)
    throw new Error(`${path} must be "soft", "natural", or "strong".`);
  return index;
}

/** Validate completely before returning a replacement draft; failures never mutate the script. */
export function importPrompt(input: string): Script {
  if (new TextEncoder().encode(input).length > MAX_IMPORT_BYTES)
    throw new Error("JSON must be smaller than 256 KB.");
  const cleaned = input
    .trim()
    .replace(/^```(?:json)?\s*\n([\s\S]*?)\n```$/i, "$1");
  let parsed: unknown;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    throw new Error(
      "This is not valid JSON. Paste the complete JSON object and check quotes and commas.",
    );
  }
  const document = object(parsed, "Script", [
    "version",
    "title",
    "mode",
    "defaults",
    "cues",
  ]);
  if (document.version !== 1) throw new Error("Script.version must be 1.");
  if (
    typeof document.title !== "string" ||
    !document.title.trim() ||
    document.title.length > 100
  )
    throw new Error("Script.title must contain 1–100 characters.");
  if (
    document.mode !== undefined &&
    document.mode !== "teleprompter" &&
    document.mode !== "cue-cards"
  )
    throw new Error('Script.mode must be "teleprompter" or "cue-cards".');
  let defaults: ObjectValue = {};
  if (document.defaults !== undefined) {
    defaults = object(document.defaults, "defaults", ["wpm", "intensity"]);
  }
  const defaultPace = pace(defaults.wpm, "defaults.wpm", 140);
  const defaultIntensity = intensity(
    defaults.intensity,
    "defaults.intensity",
    1,
  );
  if (
    !Array.isArray(document.cues) ||
    document.cues.length === 0 ||
    document.cues.length > 10000
  )
    throw new Error("cues must contain 1–10000 text cues.");
  const words: Word[] = [];
  let characterCount = 0;
  for (let index = 0, total = document.cues.length; index < total; index++) {
    const path = `cues[${index}]`;
    const cue = object(document.cues[index], path, [
      "text",
      "wpm",
      "intensity",
      "pauseAfterMs",
    ]);
    if (typeof cue.text !== "string" || !cue.text.trim())
      throw new Error(`${path}.text must contain spoken text.`);
    characterCount += cue.text.length;
    const cuePace = pace(cue.wpm, `${path}.wpm`, defaultPace);
    const cueIntensity = intensity(
      cue.intensity,
      `${path}.intensity`,
      defaultIntensity,
    );
    const pause = cue.pauseAfterMs === undefined ? 0 : cue.pauseAfterMs;
    if (
      typeof pause !== "number" ||
      !Number.isInteger(pause) ||
      pause < 0 ||
      pause > 10000
    )
      throw new Error(
        `${path}.pauseAfterMs must be an integer from 0 to 10000.`,
      );
    const cueWords = parseWords(cue.text);
    cueWords[0].cueStart = true;
    if (words.length) {
      const previous = words[words.length - 1];
      const leadingSpace = cue.text.match(/^\s*/)?.[0] || "";
      if (leadingSpace) previous.text += leadingSpace;
      else if (!/\s$/.test(previous.text)) {
        previous.text += " ";
        characterCount++;
      }
    }
    for (const word of cueWords)
      words.push({ ...word, pace: cuePace, intensity: cueIntensity });
    words[words.length - 1].pauseAfterMs = pause;
    if (characterCount > 30000 || words.length > 10000)
      throw new Error(
        "Script must be at most 30000 characters and 10000 words.",
      );
  }
  return {
    title: document.title.trim(),
    words,
    mode: (document.mode || "teleprompter") as PromptDocument["mode"],
  };
}

export const CARD_EXAMPLE_JSON = JSON.stringify(
  {
    version: 1,
    title: "An idea worth sharing",
    mode: "cue-cards",
    defaults: { wpm: 140, intensity: "natural" },
    cues: [
      { text: "The problem", intensity: "strong", pauseAfterMs: 600 },
      { text: "What changed?" },
      { text: "One thing to try today", intensity: "soft" },
    ],
  } satisfies PromptDocument,
  null,
  2,
);

export function llmPrompt(mode: PromptDocument["mode"]): string {
  if (mode !== "cue-cards") return LLM_PROMPT;
  return LLM_PROMPT.replace(EXAMPLE_JSON, CARD_EXAMPLE_JSON).replace(
    "Write what I should say, with thoughtful pacing, emphasis, and pauses.",
    "Use cue-cards mode. Write concise key ideas to speak about freely, one idea per cue, rather than a full spoken script. Include emphasis and optional pause guidance.",
  );
}
